import { CheckIcon } from 'lucide-react-native';
import { useState } from 'react';
import { Controller, Control, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

import { ExpertMultiSelect } from '@/components/ExpertMultiSelect';
import { SupportForm } from '@/components/supportForm';
import { Checkbox, CheckboxIcon, CheckboxIndicator, CheckboxLabel } from '@/components/ui/checkbox';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useExpertSelectionField } from '@/hooks/useExpertSelectionField';
import { InquiryDraftFormData } from '@/hooks/useInquiryForm';
import { segmentClient } from '@/util/analytics';
import { getUserName } from '@/util/getUserName';
import { type ExpertProfile } from '@/util/mapDirectoryProfiles';

interface ExpertSelectProps {
  control: Control<InquiryDraftFormData>;
  errors: FieldErrors<InquiryDraftFormData>;
  setValue: UseFormSetValue<InquiryDraftFormData>;
  isDisabled: boolean;
  cancerFocusId: string;
  enableExpertSelection: boolean;
  filteredExperts: ExpertProfile[];
  isLoadingFilteredExperts: boolean;
  isFetchingFilteredExperts?: boolean;
  isDraft?: boolean;
  cancerTypeLabel?: string;
  currentUser?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

/**
 * Expert selection component for inquiry forms.
 *
 * Allows users to optionally select up to 3 experts for their inquiry.
 * Uses progressive disclosure - expert selection is only available after cancer type is selected.
 *
 * **Draft Behavior:**
 * - When `isDraft` is true: checkbox is enabled while loading to prevent flicker, but disabled if loading completes with no experts
 * - Expert select component appears when checkbox is checked, even if experts are still loading
 *
 * **Empty State Message:**
 * Displayed when: cancer type selected, loading complete, no experts available, and no experts selected.
 * Hidden when: user has selected experts.
 */
export function ExpertSelect({
  control,
  errors,
  setValue,
  isDisabled,
  cancerFocusId,
  enableExpertSelection,
  filteredExperts,
  isLoadingFilteredExperts,
  isFetchingFilteredExperts = false,
  isDraft = false,
  cancerTypeLabel,
  currentUser,
}: ExpertSelectProps) {
  const [showSupportModal, setShowSupportModal] = useState(false);

  const {
    isExpertMultiSelectOpen,
    setIsExpertMultiSelectOpen,
    realExperts,
    hasExperts: cancerTypeHasExperts,
    isCheckboxDisabled,
    handleSendToAdditionalChange,
    handleMultiSelectClose,
  } = useExpertSelectionField({
    control,
    setValue,
    cancerFocusId,
    filteredExperts,
    currentUser,
    analyticsSource: 'inquiry',
    maxItems: 3,
    isDisabled,
    isLoadingFilteredExperts,
    isFetchingFilteredExperts,
    isDraft,
  });

  const expertIds = useWatch({ control, name: 'expertIds' });

  const hasSelectedExperts = (expertIds?.length || 0) > 0;

  const userName = getUserName(currentUser);

  return (
    <VStack>
      <Text
        size="sm"
        className={twJoin(
          'font-bold leading-normal',
          isDisabled ? 'text-typography-400' : 'text-typography-900'
        )}>
        Experts
      </Text>

      <Text
        size="xs"
        className={twJoin(
          'mb-3 mt-2 font-normal leading-normal',
          isDisabled ? 'text-typography-400' : 'text-typography-600'
        )}>
        Choose specific experts or let Primum choose for you.
      </Text>

      <VStack className="bg-background-50/60 mb-0 mt-0 rounded-xl border border-outline-100 p-4">
        <Controller
          name="enableExpertSelection"
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <Checkbox
                value={value ? 'true' : 'false'}
                onChange={(checked) => {
                  onChange(checked);

                  handleSendToAdditionalChange(checked);
                }}
                isChecked={value}
                isDisabled={isCheckboxDisabled}
                size="sm"
                aria-label="Send to specific experts (optional)"
                testID="enable-expert-selection-checkbox">
                <CheckboxIndicator>
                  <CheckboxIcon as={CheckIcon} />
                </CheckboxIndicator>
                <CheckboxLabel className="text-sm">Choose specific experts</CheckboxLabel>
              </Checkbox>
            );
          }}
        />

        {cancerFocusId &&
          !isLoadingFilteredExperts &&
          !cancerTypeHasExperts &&
          !hasSelectedExperts && (
            <VStack className="mt-3">
              <Text className="text-xs text-typography-500">
                We're temporarily unable to display experts for this cancer type, but if you submit
                your question, we'll get it to the right expert and respond as soon as possible.
              </Text>
            </VStack>
          )}

        {enableExpertSelection &&
          cancerFocusId &&
          (isDraft || cancerTypeHasExperts || isLoadingFilteredExperts || hasSelectedExperts) && (
            <VStack className="mt-3">
              <Controller
                name="expertIds"
                control={control}
                render={({ field: { onChange, value }, fieldState, formState }) => {
                  return (
                    <FormControl
                      isInvalid={
                        !!errors.expertIds?.message &&
                        (fieldState.isTouched || formState.isSubmitted)
                      }
                      isDisabled={isDisabled}>
                      <ExpertMultiSelect
                        value={value || []}
                        options={realExperts}
                        onChange={onChange}
                        placeholder="Select up to 3 experts"
                        isLoading={isLoadingFilteredExperts}
                        isDisabled={isDisabled}
                        currentUser={currentUser}
                        maxItems={3}
                        isOpen={isExpertMultiSelectOpen}
                        onClose={handleMultiSelectClose}
                        onSelectedItemPress={() => {
                          segmentClient.track(ANALYTICS_EVENTS.EXPERT_SELECTION_MODAL_OPENED, {
                            selectedCount: expertIds?.length || 0,
                            availableCount: realExperts.length,
                            userName,
                          });

                          setIsExpertMultiSelectOpen(true);
                        }}
                        cancerType={cancerTypeLabel}
                      />

                      {errors.expertIds && (
                        <FormControlError className="mt-2">
                          <FormControlErrorIcon />
                          <FormControlErrorText size="xs">
                            {errors.expertIds.message}
                          </FormControlErrorText>
                        </FormControlError>
                      )}
                    </FormControl>
                  );
                }}
              />
            </VStack>
          )}

        <SupportForm
          isOpen={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          initialValues={{
            name:
              currentUser?.firstName && currentUser?.lastName
                ? `${currentUser.firstName} ${currentUser.lastName}`
                : '',
            email: currentUser?.email || '',
            reason: 'Recommend an Expert',
            message: 'Hello Primum,\n\nI would like to recommend an expert.',
          }}
        />
      </VStack>

      {!enableExpertSelection ? (
        <Text size="xs" className="mt-4 font-normal text-typography-500">
          If unchecked, Primum will match your inquiry to an available subspecialist.
        </Text>
      ) : null}
    </VStack>
  );
}
